import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi, legalDocumentsApi, ApiError } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    ListPendingRequiredLegalDocumentsResponse,
    ListPendingRequiredLegalDocumentsResponseSchema,
} from '@pillar-portal/api-types';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAuth?: boolean;
}

const PUBLIC_ROUTES = [
    '/login',
    '/login/totp',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
];

export function ProtectedRoute({ children, requireAuth = true }: ProtectedRouteProps) {
    const log = createComponentLogger('ProtectedRoute');
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const queryClient = useQueryClient();

    const { user, setUser, logout: logoutStore, isLoggingOut, setIsLoggingOut } = useAuthStore();
    const [isMounted, setIsMounted] = useState(false);

    const isPublicRoute = pathname
        ? PUBLIC_ROUTES.some((route) => pathname.startsWith(route))
        : false;

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Cleanup the isLoggingOut flag when navigating to a public route
    useEffect(() => {
        if (isPublicRoute && isLoggingOut) {
            setIsLoggingOut(false);
        }
    }, [isPublicRoute, isLoggingOut, setIsLoggingOut]);

    // Cancel and reset profile query
    // Only remove queries on public routes or when explicitly logging out
    // Don't remove queries when !user on protected routes - that's normal state before profile fetch completes
    useEffect(() => {
        if (isPublicRoute || isLoggingOut) {
            runInBackground(queryClient.cancelQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error canceling queries', err);
            });
            runInBackground(queryClient.removeQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error removing queries', err);
            });
        }
    }, [isPublicRoute, isLoggingOut, queryClient]);

    const isQueryEnabled = requireAuth && isMounted && !isPublicRoute && !user && !isLoggingOut;

    const {
        data: profileData,
        error,
        isLoading,
        isFetched, // Used to track if the profile check has ever completed
    } = useQuery<SessionUserProfileDetailedResponse | null>({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await authApi.getProfile();
            if (response === null || response === undefined) {
                return null;
            }
            return SessionUserProfileDetailedResponseSchema.parse(response);
        },
        retry: false,
        enabled: isQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true, // Always refetch on mount to check auth status
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (profileData) {
            setUser(profileData);
        }
    }, [profileData, setUser]);

    const isPendingLegalDocsQueryEnabled: boolean =
        requireAuth && isMounted && !isPublicRoute && !!user && !isLoggingOut;

    const {
        data: pendingRequiredLegalDocuments,
        error: pendingRequiredLegalDocumentsError,
        isLoading: isPendingRequiredLegalDocumentsLoading,
        isFetched: isPendingRequiredLegalDocumentsFetched,
    } = useQuery<ListPendingRequiredLegalDocumentsResponse>({
        queryKey: ['pendingRequiredLegalDocuments'],
        queryFn: async () => {
            const response = await legalDocumentsApi.listPendingRequiredLegalDocuments();
            return ListPendingRequiredLegalDocumentsResponseSchema.parse(response);
        },
        retry: false,
        enabled: isPendingLegalDocsQueryEnabled,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (!requireAuth || isPublicRoute || isLoggingOut) {
            return;
        }
        if (!user) {
            return;
        }
        if (!isPendingRequiredLegalDocumentsFetched || isPendingRequiredLegalDocumentsLoading) {
            return;
        }
        if (!pendingRequiredLegalDocuments) {
            return;
        }
        if (pathname.startsWith('/legal-workflow')) {
            return;
        }
        if (pendingRequiredLegalDocuments.legal_documents.length === 0) {
            return;
        }
        const returnTo: string = encodeURIComponent(pathname);
        runInBackground(navigate(`/legal-workflow?returnTo=${returnTo}`, { replace: true }), (err) => {
            log.error('Error navigating to legal workflow', err);
        });
    }, [
        requireAuth,
        isPublicRoute,
        isLoggingOut,
        user,
        isPendingRequiredLegalDocumentsFetched,
        isPendingRequiredLegalDocumentsLoading,
        pendingRequiredLegalDocuments,
        pathname,
        navigate,
        log,
    ]);

    useEffect(() => {
        if (pendingRequiredLegalDocumentsError === null) {
            return;
        }
        log.error('Pending legal documents query failed', pendingRequiredLegalDocumentsError);
    }, [pendingRequiredLegalDocumentsError, log]);

    // Handle invalid session: 401 Unauthorized
    useEffect(() => {
        if (
            requireAuth &&
            !isPublicRoute &&
            pathname !== '/login' &&
            error &&
            error instanceof ApiError &&
            error.status === 401
        ) {
            setIsLoggingOut(true);
            runInBackground(queryClient.clear(), (err) => {
                log.error('Error clearing queries', err);
            });
            runInBackground(queryClient.removeQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error removing queries', err);
            });
            logoutStore();
            runInBackground(navigate('/login', { replace: true }), (err) => {
                log.error('Error navigating to login', err);
            });
        }
    }, [
        error,
        requireAuth,
        isPublicRoute,
        pathname,
        logoutStore,
        navigate,
        queryClient,
        setIsLoggingOut,
    ]);

    useEffect(() => {
        // Only redirect if:
        // 1. Query is/was enabled (we're on a protected route and user is null)
        // 2. Query has completed (isFetched is true AND not currently loading)
        // 3. No profile data (query failed or returned no data)
        // 4. No user was set (profile fetch failed or returned no user)
        // 5. Not currently logging out
        // 6. No 401/403 error (those are handled by the error handler above)
        const shouldRedirectToLogin =
            requireAuth &&
            !isPublicRoute &&
            !user &&
            !profileData &&
            !isLoggingOut &&
            isQueryEnabled &&
            isFetched &&
            !isLoading &&
            !(error && error instanceof ApiError && error.status === 401);

        if (shouldRedirectToLogin) {
            runInBackground(navigate('/login', { replace: true }), (err) => {
                log.error('Error navigating to login', err);
            });
        }
    }, [
        requireAuth,
        isPublicRoute,
        user,
        profileData,
        isLoggingOut,
        isQueryEnabled,
        isFetched,
        isLoading,
        error,
        navigate,
    ]);

    // --- RENDERING LOGIC ---

    // Don't block rendering on public routes (render immediately)
    if (isPublicRoute) {
        return <>{children}</>;
    }

    // Show loading state if auth is required and we are waiting for initial mount OR fetch
    if (requireAuth && (!isMounted || isLoading || isPendingRequiredLegalDocumentsLoading)) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Loading...</p>
            </div>
        );
    }

    if (
        requireAuth &&
        !isPublicRoute &&
        user &&
        pendingRequiredLegalDocuments &&
        pendingRequiredLegalDocuments.legal_documents.length > 0 &&
        !pathname.startsWith('/legal-workflow')
    ) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p>Redirecting to legal workflow...</p>
            </div>
        );
    }

    // If we've passed all checks (auth required, loading done, user is present), render children
    return <>{children}</>;
}
